import type { FocusEvent } from 'react';
import type { Key } from 'react-aria-components';
import { Button } from '@/components/Button';
import { Form } from '@/components/Form';
import { SearchableSelect } from '@/components/SearchableSelect';
import { TextField } from '@/components/TextField';
import { ButtonVariant, FieldName } from '@/constants';
import type { Address, AddressFieldKey, County, Handler } from '@/types';
import { useAddress } from './useAddress';
import * as Styled from './ShippingAddressFields.styled';

interface AddressFieldsProps {
  address: Address;
  counties?: County[];
  countyLabel: string;
  excludedBillingCountryCodes?: string[];
  handleChange: (fieldName: AddressFieldKey) => (value: Key | null) => void;
  handlePhoneBlur: (e: FocusEvent<HTMLInputElement>) => void;
  handleSubmit: Handler;
  isBillingAddress?: boolean;
  isModal?: boolean;
  isPending?: boolean;
  postalCodeLabel: string;
  validationErrors: Record<string, string>;
}

export const AddressFields = ({
  address,
  counties,
  countyLabel,
  excludedBillingCountryCodes,
  handleChange,
  handlePhoneBlur,
  handleSubmit,
  isBillingAddress = false,
  isModal = false,
  isPending = false,
  postalCodeLabel,
  validationErrors,
}: AddressFieldsProps) => {
  const { fieldData } = useAddress(isBillingAddress, excludedBillingCountryCodes);

  const annotatedCountyLabel =
    address.countryCode === 'CAN' ? `${countyLabel} (optional)` : countyLabel;

  return (
    <Form handleSubmit={handleSubmit} validationErrors={validationErrors}>
      {fieldData.map(field => {
        const isCountry = field.name === FieldName.Country;
        const isCounty = field.name === FieldName.County;
        const isPhone = field.name === FieldName.Phone;

        if (isCountry || isCounty) {
          const genericList = field.options ?? [];
          const countyList =
            counties?.map(county => ({
              id: county.countyCode,
              name: county.countyName,
            })) ?? genericList;

          return (
            <SearchableSelect
              key={field.name}
              dropdownLabel={isCounty ? annotatedCountyLabel : field.label}
              handleSelection={handleChange(field.name)}
              isRequired={field.isRequired}
              items={isCounty ? countyList : genericList}
              name={field.name}
              searchLabel={field.searchLabel ?? ''}
              value={isCounty ? address.countyCode : address.countryCode}
            />
          );
        }

        return (
          <TextField
            defaultValue={address[field.name]}
            inputMode={isPhone ? 'tel' : undefined}
            isRequired={field.isRequired}
            key={field.name}
            label={
              field.name === FieldName.PostalCode
                ? postalCodeLabel
                : field.label
            }
            name={field.name}
            onBlur={isPhone ? handlePhoneBlur : undefined}
            onChange={handleChange(field.name)}
            type="text"
          />
        );
      })}
      <input type="hidden" name="id" value={address.id ?? ''} />
      {isModal ? (
        <Styled.ButtonWrapper>
          <Button slot="close" type="button" variant={ButtonVariant.Secondary}>
            Cancel
          </Button>
          <Button isPending={isPending} type="submit">
            Submit
          </Button>
        </Styled.ButtonWrapper>
      ) : null}
    </Form>
  );
};
